import React, { createContext, useContext, useEffect, useReducer } from "react";
import { reducer } from "./useReducerAction";
import { INITIAL_STATE } from "./useReducerState";
import { ITrackContextValue } from "./useContextInterface";
import { getAdminIdFromStoreFrontId } from "../helpers/shopify";
import { sortBySku } from "../helpers/utils";

const TrackContext = createContext<ITrackContextValue | null>(null);

const useTrackContext = () => {
  const context = useContext(TrackContext);
  if (!context) {
    throw new Error("useTrackContext must be used within a TrackProvider");
  }
  return context;
};

const TrackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { battleKartSelection, mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, productRenderedLocation, shopifyCartVariants } = state;

  useEffect(() => {
    const handleCartUpdated = (event: CustomEvent) => {
      if (productRenderedLocation !== "external") {
        const {
          detail: { cart },
        } = event;

        const { items_subtotal_price } = cart;

        if (items_subtotal_price) {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: items_subtotal_price,
            },
          });
        } else {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: "0",
            },
          });
        }

        if (cart && cart.items && cart.items.length > 0) {
          const cartItems = cart.items;
          const cartVariantIds = cartItems.map((item: any) => item.id);
          const battleKartSelectedVariants = battleKartSelection.variantsAndDate.selectedVariants.filter((item: any) =>
            cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id))
          );
          const mainTrackSelectedVariants = mainTrackSelection.variantsAndDate.selectedVariants.filter((item: any) =>
            cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id))
          );
          const miniTrackSelectedVariants = miniTrackSection.variantsAndDate.selectedVariants.filter((item: any) =>
            cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id))
          );
          const vrSelectedVariants = vrSelection.variantsAndDate.selectedVariants.filter((item: any) =>
            cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id))
          );
          const juniorTrackSelectedVariants = juniorTrackSelection.variantsAndDate.selectedVariants.filter((item: any) =>
            cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id))
          );

          if (battleKartSelectedVariants.length > 0) {
            const newBattleKartSelectedVariants = {
              ...battleKartSelection.variantsAndDate,
              selectedSessionTime: battleKartSelection.variantsAndDate.selectedSessionTime,
              selectedDate: battleKartSelection.variantsAndDate.selectedDate,
              selectedVariants: sortBySku([...battleKartSelectedVariants], "BATTLE_KART_TRACK"),
            };

            dispatch({
              type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
              payload: {
                variantsAndDate: newBattleKartSelectedVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
              payload: {
                variantsAndDate: {
                  ...battleKartSelection.variantsAndDate,
                  selectedSessionTime: battleKartSelection.variantsAndDate.selectedSessionTime,
                  selectedDate: battleKartSelection.variantsAndDate.selectedDate,
                  selectedVariants: [],
                },
              },
            });
          }

          if (mainTrackSelectedVariants.length > 0) {
            const newMainTrackSelectedVariants = {
              ...mainTrackSelection.variantsAndDate,
              selectedSessionTime: mainTrackSelection.variantsAndDate.selectedSessionTime,
              selectedDate: mainTrackSelection.variantsAndDate.selectedDate,
              selectedVariants: sortBySku([...mainTrackSelectedVariants], "MAIN_TRACK"),
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: newMainTrackSelectedVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: {
                  ...mainTrackSelection.variantsAndDate,
                  selectedSessionTime: mainTrackSelection.variantsAndDate.selectedSessionTime,
                  selectedDate: mainTrackSelection.variantsAndDate.selectedDate,
                  selectedVariants: [],
                },
              },
            });
          }

          if (miniTrackSelectedVariants.length > 0) {
            const newMiniTrackSelectedVariants = {
              ...miniTrackSection.variantsAndDate,
              selectedSessionTime: miniTrackSection.variantsAndDate.selectedSessionTime,
              selectedDate: miniTrackSection.variantsAndDate.selectedDate,
              selectedVariants: sortBySku([...miniTrackSelectedVariants], "MINI_TRACK"),
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT_MINI_TRACK",
              payload: {
                variantsAndDate: newMiniTrackSelectedVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_DATE_AND_VARIANT_MINI_TRACK",
              payload: {
                variantsAndDate: {
                  ...miniTrackSection.variantsAndDate,
                  selectedSessionTime: miniTrackSection.variantsAndDate.selectedSessionTime,
                  selectedDate: miniTrackSection.variantsAndDate.selectedDate,
                  selectedVariants: [],
                },
              },
            });
          }

          if (vrSelectedVariants.length > 0) {
            const newVrSelectedVariants = {
              ...vrSelection.variantsAndDate,
              selectedSessionTime: vrSelection.variantsAndDate.selectedSessionTime,
              selectedDate: vrSelection.variantsAndDate.selectedDate,
              selectedVariants: sortBySku([...vrSelectedVariants], "VIRTUAL_REALITY"),
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
              payload: {
                variantsAndDate: newVrSelectedVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
              payload: {
                variantsAndDate: {
                  ...vrSelection.variantsAndDate,
                  selectedSessionTime: vrSelection.variantsAndDate.selectedSessionTime,
                  selectedDate: vrSelection.variantsAndDate.selectedDate,
                  selectedVariants: [],
                },
              },
            });
          }

          if (juniorTrackSelectedVariants.length > 0) {
            const newJuniorTrackSelectedVariants = {
              ...juniorTrackSelection.variantsAndDate,
              selectedSessionTime: juniorTrackSelection.variantsAndDate.selectedSessionTime,
              selectedDate: juniorTrackSelection.variantsAndDate.selectedDate,
              selectedVariants: sortBySku([...juniorTrackSelectedVariants], "JUNIOR_TRACK"),
            };

            dispatch({
              type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
              payload: {
                variantsAndDate: newJuniorTrackSelectedVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
              payload: {
                variantsAndDate: {
                  ...juniorTrackSelection.variantsAndDate,
                  selectedSessionTime: juniorTrackSelection.variantsAndDate.selectedSessionTime,
                  selectedDate: juniorTrackSelection.variantsAndDate.selectedDate,
                  selectedVariants: [],
                },
              },
            });
          }

          const updatedShopifyCartVariants = shopifyCartVariants.filter((variant: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id)));

          if (updatedShopifyCartVariants.length > 0) {
            dispatch({
              type: "SET_SHOPIFY_CART_VARIANTS",
              payload: {
                shopifyCartVariants: updatedShopifyCartVariants,
              },
            });
          } else {
            dispatch({
              type: "SET_SHOPIFY_CART_VARIANTS",
              payload: {
                shopifyCartVariants: [],
              },
            });
          }
        } else {
          dispatch({
            type: "SET_SHOPIFY_CART_VARIANTS",
            payload: {
              shopifyCartVariants: [],
            },
          });
          dispatch({
            type: "SET_DATE_AND_VARIANT",
            payload: {
              variantsAndDate: {
                ...mainTrackSelection.variantsAndDate,
                selectedSessionTime: mainTrackSelection.variantsAndDate.selectedSessionTime,
                selectedDate: mainTrackSelection.variantsAndDate.selectedDate,
                selectedVariants: [],
              },
            },
          });

          dispatch({
            type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
            payload: {
              variantsAndDate: {
                ...battleKartSelection.variantsAndDate,
                selectedSessionTime: battleKartSelection.variantsAndDate.selectedSessionTime,
                selectedDate: battleKartSelection.variantsAndDate.selectedDate,
                selectedVariants: [],
              },
            },
          });

          dispatch({
            type: "SET_DATE_AND_VARIANT_MINI_TRACK",
            payload: {
              variantsAndDate: {
                ...miniTrackSection.variantsAndDate,
                selectedSessionTime: miniTrackSection.variantsAndDate.selectedSessionTime,
                selectedDate: miniTrackSection.variantsAndDate.selectedDate,
                selectedVariants: [],
              },
            },
          });

          dispatch({
            type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
            payload: {
              variantsAndDate: {
                ...vrSelection.variantsAndDate,
                selectedSessionTime: vrSelection.variantsAndDate.selectedSessionTime,
                selectedDate: vrSelection.variantsAndDate.selectedDate,
                selectedVariants: [],
              },
            },
          });

          dispatch({
            type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
            payload: {
              variantsAndDate: {
                ...juniorTrackSelection.variantsAndDate,
                selectedSessionTime: juniorTrackSelection.variantsAndDate.selectedSessionTime,
                selectedDate: juniorTrackSelection.variantsAndDate.selectedDate,
                selectedVariants: [],
              },
            },
          });
        }
      }
    };
    window.addEventListener("cart:updated", handleCartUpdated as EventListener);

    return () => {
      window.removeEventListener("cart:updated", handleCartUpdated as EventListener);
    };
  }, [
    dispatch,
    productRenderedLocation,
    battleKartSelection.variantsAndDate,
    juniorTrackSelection.variantsAndDate,
    mainTrackSelection.variantsAndDate,
    miniTrackSection.variantsAndDate,
    vrSelection.variantsAndDate,
    shopifyCartVariants,
  ]);

  return <TrackContext.Provider value={{ ...state, dispatch }}>{children}</TrackContext.Provider>;
};

export { useTrackContext, TrackProvider };
